import { Icon, Spin, Tree } from 'antd';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { UrgentIcon } from '../../../../../../../../resource/icons';
import styles from './ServiceSetsSection.module.scss';
import {
  getReasonText,
  getSelectedReasonIds,
  isServiceReasonImmediate,
} from './utils';

const { TreeNode } = Tree;

const getServiceSetPreviewKey = (prefix, value, index) =>
  `${prefix}-${value ?? index}`;

const ServiceSetPreviewTitle = ({ children, onClick }) => {
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onClick(event);
  };

  return (
    <div
      className={styles.serviceSetPreviewToggle}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

const renderServiceSetPreviewService = ({ nodeKey, service, onTitleClick }) => {
  const reasonIds = getSelectedReasonIds(service);

  return (
    <TreeNode
      key={nodeKey}
      title={
        <ServiceSetPreviewTitle
          onClick={(event) => onTitleClick(event, nodeKey)}
        >
          <div className={styles.serviceSetPreviewNode}>
            {service.serviceName || service.serviceId}
          </div>
        </ServiceSetPreviewTitle>
      }
    >
      {reasonIds.length ? (
        reasonIds.map((reasonId, index) => (
          <TreeNode
            icon={<Icon type="tag" />}
            key={getServiceSetPreviewKey(`${nodeKey}-reason`, reasonId, index)}
            title={
              <div className={styles.serviceSetPreviewReason}>
                {getReasonText(service, reasonId, index)}
                {isServiceReasonImmediate(service, reasonId, index) && (
                  <span className={styles.urgentIconWrapper}>
                    <UrgentIcon className={styles.urgentIcon} />
                  </span>
                )}
              </div>
            }
          />
        ))
      ) : (
        <TreeNode
          disabled
          key={`${nodeKey}-empty`}
          title={
            <div className={styles.serviceSetPreviewEmpty}>
              Причины не выбраны
            </div>
          }
        />
      )}
    </TreeNode>
  );
};

export const ServiceSetsPreview = ({
  className,
  serviceSets = [],
  emptyText,
  isLoading = false,
}) => {
  const previewServices = useMemo(
    () =>
      serviceSets.flatMap((serviceSet, serviceSetIndex) =>
        (serviceSet.services || []).map((service, serviceIndex) => ({
          service,
          key: getServiceSetPreviewKey(
            `service-set-${
              serviceSet.id || serviceSet.name || serviceSetIndex
            }`,
            service.serviceId || service.serviceName,
            serviceIndex
          ),
        }))
      ),
    [serviceSets]
  );
  const hasPreviewServices = Boolean(previewServices.length);
  const initialExpandedKeys = useMemo(() => {
    if (!hasPreviewServices) {
      return [];
    }

    return previewServices.map(({ key }) => key);
  }, [hasPreviewServices, previewServices]);
  const [expandedKeys, setExpandedKeys] = useState(initialExpandedKeys);

  useEffect(() => {
    setExpandedKeys(initialExpandedKeys);
  }, [initialExpandedKeys]);

  const togglePreviewNode = useCallback((nodeKey) => {
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(nodeKey)
        ? prevKeys.filter((key) => key !== nodeKey)
        : [...prevKeys, nodeKey]
    );
  }, []);

  const handleTitleClick = useCallback(
    (event, nodeKey) => {
      event.stopPropagation();
      togglePreviewNode(nodeKey);
    },
    [togglePreviewNode]
  );

  return (
    <div className={cn(styles.serviceSetPreview, className)}>
      <div className={styles.serviceSetPreviewScroll}>
        {serviceSets.map((serviceSet, index) => {
          const comment = (serviceSet.comment || '').trim();

          return comment ? (
            <div
              className={styles.serviceSetPreviewComment}
              key={getServiceSetPreviewKey('comment', serviceSet.id, index)}
            >
              <div className={styles.serviceSetPreviewCommentLabel}>
                {serviceSets.length > 1
                  ? `${serviceSet.name} — комментарий`
                  : 'Комментарий'}
              </div>
              <div>{comment}</div>
            </div>
          ) : null;
        })}
        {isLoading ? (
          <div className={styles.serviceSetPreviewLoader}>
            <Spin size="small" />
          </div>
        ) : !hasPreviewServices ? (
          <div className={styles.serviceSetPreviewEmpty}>{emptyText}</div>
        ) : (
          <Tree
            className={styles.serviceSetPreviewTree}
            expandedKeys={expandedKeys}
            selectable={false}
            onExpand={setExpandedKeys}
          >
            {previewServices.map(({ key, service }) =>
              renderServiceSetPreviewService({
                nodeKey: key,
                service,
                onTitleClick: handleTitleClick,
              })
            )}
          </Tree>
        )}
      </div>
    </div>
  );
};

export const CreateServiceSetPreview = ({
  serviceSetName,
  serviceSetComment,
  previewServices,
}) => (
  <ServiceSetsPreview
    emptyText="Выберите услуги и причины"
    serviceSets={[
      {
        id: 'new-service-set',
        name: serviceSetName || 'Новый набор услуг',
        comment: serviceSetComment,
        services: previewServices,
      },
    ]}
  />
);
